import { toast } from "react-toastify";
import Swal from 'sweetalert2';
import { useFetchAllIndustriesList } from "../../hooks/useIndustry";
import {
    LanguageSwitcher,
    IndustrySelector,
    FormInput,
    StepInput,
    StepsList,
    TouchpointForm,
    EditTouchpointForm,
    FormActions
} from "./OperationJourneyFormComponents";
import { useAddOperationJourney, useUpdateOperationJourney } from "../../hooks/useOperationJourney";
import { useOperationJourneyForm } from "./useOperationJourneyForm";

const langLabels = { 
    eng: 'English', 
    ar: 'Arabic', 
    esp: 'Spanish', 
    it: 'Italian', 
    det: 'German', 
    fr: 'French' 
};

const OperationJourneyModal = ({ 
    mode = 'create',
    onClose, 
    selectedOperation = null 
}) => {
    const { data } = useFetchAllIndustriesList();
    const addMutation = useAddOperationJourney();
    const updateMutation = useUpdateOperationJourney();

    const {
        formMethods,
        steps,
        currentLang,
        currentLangIndex,
        setCurrentLangIndex,
        activeStepIndex,
        setActiveStepIndex,
        newTitle,
        setNewTitle,
        updatedTitle,
        setUpdatedTitle,
        title,
        setTitle,
        editingTouchpointId,
        setEditingTouchpointId,
        curvePos,
        setCurvePos,
        svgRef,
        isTranslating,
        areAllLangsFilled,
        bezier,
        startDrag,
        handleStepTitleChange,
        handleTouchpointTitleChange,
        handleAddStep,
        handleRemoveStep,
        handleAddTouchpoint,
        handleSaveUpdatedTouchpoint,
        handleDeleteTouchpoint,
        translateAllFromEnglish,
        prepareSubmitData,
        langKeys,
        languages,
    } = useOperationJourneyForm(mode === 'update' ? selectedOperation : null);

    const { register, handleSubmit, watch } = formMethods;

    const handleRemoveStepWithConfirmation = (stepIndex) => {
        Swal.fire({
            title: "Are you sure?",
            text: "You won't be able to undo this action!",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#3085d6",
            cancelButtonColor: "#d33",
            confirmButtonText: "Yes, remove it!"
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    handleRemoveStep(stepIndex);
                    Swal.fire({
                        title: "Removed!",
                        text: "The step has been successfully removed.",
                        icon: "success"
                    });
                } catch (error) {
                    Swal.fire({
                        title: "Error!",
                        text: "An unexpected error occurred. Please try again later.",
                        icon: "error"
                    });
                    console.error("Error removing step:", error);
                }
            }
        });
    };

    const handleEditTouchpoint = (stepIndex, tpIndex) => {
        const titles = {};
        let currentPos = 0.5;
        languages.forEach((lang) => {
            const { tpKey, titleKey } = langKeys[lang];
            const touchpoint = steps[lang]?.[stepIndex]?.[tpKey]?.[tpIndex];
            console.log(touchpoint)
            titles[lang] = touchpoint?.[titleKey] || "";
            if (touchpoint?.pos !== undefined) {
                currentPos = parseFloat(touchpoint.pos) || 0.5;
            }
        });
        setUpdatedTitle(titles);
        setCurvePos(currentPos);
        setEditingTouchpointId({ stepIndex, tpIndex });
    };

    const handleDeleteTouchpointWithConfirmation = (stepIndex, tpIndex) => {
        Swal.fire({
            title: "Are you sure?",
            text: "You won't be able to undo this action!",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#3085d6",
            cancelButtonColor: "#d33",
            confirmButtonText: "Yes, delete it!"
        }).then((result) => {
            if (result.isConfirmed) {
                try {
                    handleDeleteTouchpoint(stepIndex, tpIndex);
                    Swal.fire({
                        title: "Deleted!",
                        text: "The touchpoint has been successfully removed.",
                        icon: "success"
                    });
                } catch (error) {
                    Swal.fire({
                        title: "Error!",
                        text: "An unexpected error occurred. Please try again later.",
                        icon: "error"
                    });
                    console.error("Error deleting touchpoint:", error);
                }
            }
        });
    };

    const onSubmit = (formData) => {
        if (!formData.industry) return toast.info("Please select industry in all languages");
        if (!areAllLangsFilled(formData.name)) return toast.info("Please fill name in all languages");
        if (!areAllLangsFilled(formData.description)) return toast.info("Please fill description in all languages");
        if (!steps || Object.keys(steps).length === 0) return toast.info("Add at least 1 step");

        const journeyData = prepareSubmitData(formData);

        if (mode === 'create') {
            addMutation.mutate(journeyData, {
                onSuccess: () => {
                    onClose();
                },
            });
        } else {
            updateMutation.mutate(
                { id: selectedOperation?.id, data: journeyData },
                {
                    onSuccess: () => {
                        onClose();
                    },
                }
            );
        }
    };

    const isSubmitDisabled = 
        !areAllLangsFilled(watch('name')) || 
        !areAllLangsFilled(watch('description'));

    return (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-50 flex justify-center items-center z-50">
            <div className="bg-white rounded-lg p-6 w-1/2 max-h-[80vh] overflow-y-auto shadow-lg">
                <div className="flex justify-between">
                    <h2 className="text-2xl font-semibold mb-4">
                        {mode === 'create' ? 'Create' : 'Update'} Operation Journey
                    </h2>
                    <h2 
                        className="text-2xl font-semibold mb-4 cursor-pointer" 
                        onClick={onClose}
                    >
                        X
                    </h2>
                </div>

                <LanguageSwitcher
                    languages={languages}
                    currentIndex={currentLangIndex}
                    onSwitch={setCurrentLangIndex}
                    langLabels={langLabels}
                />

                <form onSubmit={handleSubmit(onSubmit)}>
                    <IndustrySelector
                        currentLang={currentLang}
                        register={register}
                        data={data}
                    />

                    <FormInput
                        currentLang={currentLang}
                        fieldName="name"
                        register={register}
                    />

                    <FormInput
                        currentLang={currentLang}
                        fieldName="description"
                        register={register}
                    />

                    <StepInput
                        currentLang={currentLang}
                        value={title[currentLang]}
                        onChange={(e) => setTitle({ ...title, [currentLang]: e.target.value })}
                        onAdd={handleAddStep}
                    />

                    <div className="grid grid-cols-2 gap-4 mb-6">
                        <div className="col-span-2">
                            <div className="grid grid-cols-1 gap-2">
                                <label className="text-left text-lg font-semibold">
                                    {currentLang === 'ar' ? 'المراحل' : 'Steps'}
                                </label>
                            </div>
                            <StepsList
                                steps={steps}
                                currentLang={currentLang}
                                langKeys={langKeys}
                                onStepTitleChange={handleStepTitleChange}
                                onStepClick={setActiveStepIndex}
                                onRemoveStep={handleRemoveStepWithConfirmation}
                                onTouchpointTitleChange={handleTouchpointTitleChange}
                                onDeleteTouchpoint={handleDeleteTouchpointWithConfirmation}
                                onEditTouchpoint={handleEditTouchpoint}
                            />

                            {activeStepIndex !== null && (
                                <TouchpointForm
                                    currentLang={currentLang}
                                    title={newTitle[currentLang]}
                                    onTitleChange={(e) => setNewTitle({ ...newTitle, [currentLang]: e.target.value })}
                                    onAdd={() => handleAddTouchpoint(activeStepIndex)}
                                    curvePos={curvePos}
                                    svgRef={svgRef}
                                    bezier={bezier}
                                    startDrag={startDrag}
                                />
                            )}
                        </div>
                    </div>

                    {editingTouchpointId !== null && (
                        <EditTouchpointForm
                            currentLang={currentLang}
                            title={updatedTitle[currentLang] || ""}
                            onTitleChange={(e) => setUpdatedTitle({ ...updatedTitle, [currentLang]: e.target.value })}
                            onSave={handleSaveUpdatedTouchpoint}
                            curvePos={curvePos}
                            svgRef={svgRef}
                            bezier={bezier}
                            startDrag={startDrag}
                        />
                    )}

                    <FormActions
                        currentLangIndex={currentLangIndex}
                        onPrev={() => setCurrentLangIndex(Math.max(0, currentLangIndex - 1))}
                        onNext={() => setCurrentLangIndex(Math.min(5, currentLangIndex + 1))}
                        onTranslate={translateAllFromEnglish}
                        isTranslating={isTranslating}
                        onSubmit={handleSubmit(onSubmit)}
                        isSubmitDisabled={isSubmitDisabled}
                        submitLabel={mode === 'create' ? 'Create' : 'Update'}
                        showTranslate={currentLang === "eng"}
                    />
                </form>
            </div>
        </div>
    );
};

export default OperationJourneyModal;